import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { useAuth } from "@clerk/clerk-react";
import {
  budgetsApi,
  CreateBudgetRequest,
  UpdateBudgetRequest,
} from "../../api/budgetsApi";
import BudgetForm, {
  BudgetFormData,
} from "../../components/budgets/BudgetForm";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import PageHeader from "../../components/common/PageHeader";
import { Budget, BudgetLine } from "@akfdev-com/shared";

/**
 * Budget Create/Edit Page
 *
 * Handles both creating new budgets and editing existing ones.
 * - For creation: No ID in URL, initializes empty form
 * - For editing: ID in URL, loads existing budget data
 *
 * **Validates: Requirements 1.1, 2.1, 2.2, 3.1, 3.2, 3.3**
 */
const BudgetCreateEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const { getToken } = useAuth();

  const [budget, setBudget] = useState<Budget | null>(null);
  const [lines, setLines] = useState<BudgetLine[]>([]);
  const [loading, setLoading] = useState(id ? true : false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!id;

  // Load existing budget if editing
  useEffect(() => {
    if (!isEditMode || !currentBuilding?.id) return;

    const fetchBudget = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          throw new Error("Authentication token not available");
        }

        const budgetData = await budgetsApi.get(currentBuilding.id, id, token);
        setBudget(budgetData);

        // Also load the budget lines
        const linesData = await budgetsApi.getLines(
          currentBuilding.id,
          id,
          token,
        );
        setLines(linesData);
      } catch (err) {
        console.error("Error fetching budget:", err);
        setError("Failed to load budget. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBudget();
  }, [id, isEditMode, currentBuilding?.id, getToken]);

  const handleSubmit = async (formData: BudgetFormData) => {
    if (!currentBuilding?.id) {
      setError("Building not selected");
      return;
    }

    try {
      setIsSubmitting(true);
      const token = await getToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      if (isEditMode && id) {
        // Update existing budget
        const updateData: UpdateBudgetRequest = {
          name: formData.name,
          startDate: formData.startDate,
          endDate: formData.endDate,
          budgetType: formData.budgetType,
          notes: formData.notes,
        };

        await budgetsApi.update(currentBuilding.id, id, updateData, token);
      } else {
        // Create new budget
        const createData: CreateBudgetRequest = {
          name: formData.name,
          startDate: formData.startDate,
          endDate: formData.endDate,
          budgetType: formData.budgetType,
          notes: formData.notes,
          lines: formData.lines.map((line) => ({
            categoryId: line.categoryId,
            budgetAmount: line.budgetAmount,
            notes: line.notes,
          })),
        };

        const newBudget = await budgetsApi.create(
          currentBuilding.id,
          createData,
          token,
        );
        navigate(`/dashboard/budgets/${newBudget.id}`);
      }

      // For edit mode, navigate back to details
      if (isEditMode) {
        navigate(`/dashboard/budgets/${id}`);
      }
    } catch (err) {
      console.error("Error saving budget:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save budget. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/dashboard/budgets/${id}`);
    } else {
      navigate("/dashboard/budgets");
    }
  };

  if (loading) {
    return <Spinner message="Loading budget..." />;
  }

  if (error && isEditMode) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? "Edit Budget" : "Create Budget"}
        description={
          isEditMode
            ? "Update budget details and lines"
            : `Create a new budget for ${
                currentBuilding?.name || "your building"
              }`
        }
      />

      {error && !isEditMode && (
        <div className="mt-4">
          <ErrorAlert message={error} />
        </div>
      )}

      <div className="mt-8 bg-white shadow rounded-lg p-6">
        <BudgetForm
          initialBudget={isEditMode ? budget || undefined : undefined}
          initialLines={isEditMode ? lines : undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isSubmitting}
          submitButtonLabel={isEditMode ? "Update Budget" : "Create Budget"}
        />
      </div>
    </div>
  );
};

export default BudgetCreateEdit;
